/* global setTimeout, clearTimeout */
/**
 * Behaviour hooks of Bobo, the Happy mascot, for React Native: the idle cycle and the touch reactions
 * (ports of the admin `useIdleActions` / `useBoboInteractions`; the timing rules live in `boboLogic.js` and
 * `idleMachine.js`, so this file only wires them to React and to timers).
 *
 * @module components/bobo/useBoboBehavior
 */

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useReducedMotion} from 'react-native-reanimated';
import {createIdleMachine} from './idleMachine';
import {BURST_MS, REACTION_MS, registerTap} from './boboLogic';

/** Max bursts alive at once, so a tap storm cannot flood the screen. */
const MAX_BURSTS = 3;

/** Holding the finger down this long pets Bobo. */
export const PET_PRESS_MS = 600;

/**
 * Drives Bobo's idle cycle: after 5 s without a touch he waves, looks around, ponders, hops, dances, turns his
 * back, sits down and dozes off. A poke (any touch) lets the running motion finish. Off with reduced motion.
 *
 * @param {Object} [options] Options.
 * @param {boolean} [options.enabled] Turn the cycle on/off.
 * @returns {{ action: (string|null), poke: function(): void }} The active action and a manual activity signal.
 */
export function useBoboIdle({enabled = true} = {}) {
    const reduced = useReducedMotion();
    const [action, setAction] = useState(null);
    const machineRef = useRef(null);

    useEffect(() => {
        if (!enabled || reduced) {
            setAction(null);

            return undefined;
        }
        const machine = createIdleMachine({onChange: setAction});

        machineRef.current = machine;
        machine.start();

        return () => {
            machine.stop();
            machineRef.current = null;
        };
    }, [enabled, reduced]);

    const poke = useCallback(() => {
        if (machineRef.current) {
            machineRef.current.poke();
        }
    }, []);

    return {action, poke};
}

/**
 * Bobo reacts to being touched: press → squished like a soft toy, tap → squash-and-stretch hop with sparkles,
 * two quick taps → a twirl, tapping like crazy → dizzy, holding → petted (hearts). Returns plain data for
 * `deriveBoboPose` plus the `Pressable` handlers. Off with reduced motion.
 *
 * @param {Object} [options] Options.
 * @param {boolean} [options.enabled] Turn the reactions on/off.
 * @param {function(): void} [options.onTouch] Called on every touch (e.g. to poke the idle machine).
 * @returns {{ reaction: (string|null), bursts: Array<{id: number, kind: string}>, handlers: Object }} State and handlers.
 */
export function useBoboTouch({enabled = true, onTouch} = {}) {
    const reduced = useReducedMotion();
    const [reaction, setReaction] = useState(null);
    const [bursts, setBursts] = useState([]);
    const reactionTimer = useRef(null);
    const burstTimers = useRef([]);
    const history = useRef([]);
    const nextId = useRef(1);
    const live = useRef(true);
    const active = enabled && !reduced;

    const burst = useCallback((kind) => {
        const id = nextId.current;

        nextId.current += 1;
        setBursts((list) => [...list.slice(-(MAX_BURSTS - 1)), {id, kind}]);
        burstTimers.current.push(setTimeout(() => {
            if (live.current) {
                setBursts((list) => list.filter((item) => item.id !== id));
            }
        }, BURST_MS));
    }, []);

    const play = useCallback((name, kind) => {
        clearTimeout(reactionTimer.current);
        setReaction(name);
        if (kind) {
            burst(kind);
        }
        reactionTimer.current = setTimeout(() => {
            if (live.current) {
                setReaction(null);
            }
        }, REACTION_MS[name] || 800);
    }, [burst]);

    useEffect(() => {
        live.current = true;
        const timers = burstTimers.current;

        return () => {
            live.current = false;
            clearTimeout(reactionTimer.current);
            timers.forEach((timer) => clearTimeout(timer));
        };
    }, []);

    const handlers = useMemo(() => ({
        onPressIn: () => {
            if (onTouch) {
                onTouch();
            }
            if (active) {
                clearTimeout(reactionTimer.current);
                setReaction('squish');
            }
        },
        onPressOut: () => setReaction((current) => (current === 'squish' ? null : current)),
        onPress: () => {
            if (!active) {
                return;
            }
            const result = registerTap(history.current, Date.now());

            history.current = result.history;
            play(result.reaction, result.reaction === 'hop' ? 'spark' : 'star');
        },
        onLongPress: () => {
            if (active) {
                play('love', 'heart');
            }
        },
        delayLongPress: PET_PRESS_MS,
    }), [active, onTouch, play]);

    return {reaction, bursts, handlers};
}
